// src/services/phoneInvitationService.ts
import { getAuthHeaders } from '../utils/auth';

export interface PhoneInvitation {
   tripId?: string;
   eventId?: string;
   albumId?: string;
   phoneNumber: string;
   role: string;
   customMessage?: string;
   recipientName?: string;
}

export interface PhoneInvitationResponse {
   success: boolean;
   message: string;
   invitationId: string;
   smsSent: boolean;
   smsError?: string;
}

export interface InvitationVerificationResponse {
   success: boolean;
   invitation?: {
      id: string;
      phoneNumber: string;
      tripId?: string;
      eventId?: string;
      albumId?: string;
      tripTitle?: string;
      eventTitle?: string;
      albumTitle?: string;
      inviterName: string;
      role: string;
      type: 'trip' | 'event' | 'album';
      customMessage?: string;
      recipientName?: string;
   };
   error?: string;
}

/**
 * Send a trip phone invitation
 */
export const sendTripPhoneInvitation = async (
   invitation: PhoneInvitation
): Promise<PhoneInvitationResponse> => {
   try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/trip/invite-phone`, {
         method: 'POST',
         headers,
         body: JSON.stringify({
            tripId: invitation.tripId,
            phoneNumber: invitation.phoneNumber,
            role: invitation.role,
            customMessage: invitation.customMessage || '',
            recipientName: invitation.recipientName || '',
         }),
      });

      if (!response.ok) {
         const errorData = await response.json();
         throw new Error(errorData.error || 'Failed to send phone invitation');
      }

      const result = await response.json();
      return result;
   } catch (error) {
      console.error('Error sending trip phone invitation:', error);
      throw error;
   }
};

/**
 * Send an event phone invitation
 */
export const sendEventPhoneInvitation = async (
   invitation: PhoneInvitation
): Promise<PhoneInvitationResponse> => {
   try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/event/invite-phone`, {
         method: 'POST',
         headers,
         body: JSON.stringify({
            eventId: invitation.eventId,
            phoneNumber: invitation.phoneNumber,
            role: invitation.role,
            customMessage: invitation.customMessage || '',
            recipientName: invitation.recipientName || '',
         }),
      });

      if (!response.ok) {
         const errorData = await response.json();
         throw new Error(errorData.error || 'Failed to send phone invitation');
      }

      const result = await response.json();
      return result;
   } catch (error) {
      console.error('Error sending event phone invitation:', error);
      throw error;
   }
};

/**
 * Send an album phone invitation
 */
export const sendAlbumPhoneInvitation = async (
   invitation: PhoneInvitation
): Promise<PhoneInvitationResponse> => {
   try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/album/invite-phone`, {
         method: 'POST',
         headers,
         body: JSON.stringify({
            albumId: invitation.albumId,
            phoneNumber: invitation.phoneNumber,
            role: invitation.role,
            customMessage: invitation.customMessage || '',
            recipientName: invitation.recipientName || '',
         }),
      });

      if (!response.ok) {
         const errorData = await response.json();
         throw new Error(errorData.error || 'Failed to send phone invitation');
      }

      const result = await response.json();
      return result;
   } catch (error) {
      console.error('Error sending album phone invitation:', error);
      throw error;
   }
};

/**
 * Verify phone invitation token
 */
export const verifyPhoneInvitation = async (
   token: string
): Promise<InvitationVerificationResponse> => {
   try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/verify-phone-invitation`, {
         method: 'POST',
         headers: {
            'Content-Type': 'application/json',
         },
         body: JSON.stringify({ token }),
      });

      if (!response.ok) {
         const errorData = await response.json();
         throw new Error(errorData.error || 'Failed to verify invitation');
      }

      const result = await response.json();
      return result;
   } catch (error) {
      console.error('Error verifying phone invitation:', error);
      throw error;
   }
};

/**
 * Format phone number for display
 */
export const formatPhoneNumberForDisplay = (phoneNumber: string): string => {
   // Remove all non-digit characters
   const cleaned = phoneNumber.replace(/\D/g, '');

   // Format US phone numbers
   if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
   } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
   } else {
      // For international numbers, just add spaces for readability
      if (cleaned.length > 6) {
         return `+${cleaned.slice(0, -6)} ${cleaned.slice(-6, -4)} ${cleaned.slice(-4)}`;
      }
      return `+${cleaned}`;
   }
};

/**
 * Validate phone number format
 */
export const validatePhoneNumber = (phoneNumber: string): { isValid: boolean; error?: string } => {
   const cleaned = phoneNumber.replace(/\D/g, '');

   if (cleaned.length === 0) {
      return { isValid: false, error: 'Phone number is required' };
   }

   if (cleaned.length < 7) {
      return { isValid: false, error: 'Phone number is too short' };
   }

   if (cleaned.length > 15) {
      return { isValid: false, error: 'Phone number is too long' };
   }

   // US phone number validation
   if (cleaned.length === 10 || (cleaned.length === 11 && cleaned.startsWith('1'))) {
      const areaCode = cleaned.length === 10 ? cleaned.slice(0, 3) : cleaned.slice(1, 4);
      if (areaCode.startsWith('0') || areaCode.startsWith('1')) {
         return { isValid: false, error: 'Invalid area code' };
      }
   }

   // Nigerian phone number validation (11 digits without country code)
   if (cleaned.length === 11) {
      // Nigerian numbers typically start with 070, 080, 081, 090, 091, etc.
      const firstThreeDigits = cleaned.slice(0, 3);
      const validPrefixes = ['070', '080', '081', '090', '091', '071', '082', '083', '084', '085', '086', '087', '088', '089', '092', '093', '094', '095', '096', '097', '098', '099'];
      
      if (!validPrefixes.includes(firstThreeDigits)) {
         return { isValid: false, error: 'Invalid Nigerian phone number format' };
      }
   }

   return { isValid: true };
};

export default {
   sendTripPhoneInvitation,
   sendEventPhoneInvitation,
   sendAlbumPhoneInvitation,
   verifyPhoneInvitation,
   formatPhoneNumberForDisplay,
   validatePhoneNumber,
};
